import { useEffect } from 'react';
import confetti from 'canvas-confetti';
import { Achievement } from '@/types/achievements';
import { AchievementBadge } from './AchievementBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles } from 'lucide-react';

interface AchievementNotificationProps {
  achievement: Achievement;
  xpGained: number;
  onClose?: () => void;
}

export function AchievementNotification({ achievement, xpGained, onClose }: AchievementNotificationProps) {
  useEffect(() => {
    // Trigger confetti animation
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval: NodeJS.Timeout = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }

      const particleCount = 50 * (timeLeft / duration);
      
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);

    // Auto-dismiss after 5 seconds
    const dismissTimer = setTimeout(() => {
      onClose?.();
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(dismissTimer);
    };
  }, [onClose]);

  return (
    <Card className="w-full max-w-md mx-auto shadow-elegant border-2 border-primary/20 bg-gradient-subtle animate-scale-in">
      <CardContent className="pt-6 pb-6">
        <div className="flex flex-col items-center text-center space-y-4">
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full animate-pulse" />
            <AchievementBadge 
              achievement={achievement} 
              unlocked={true} 
              size="lg"
            />
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              <h3 className="text-2xl font-bold text-primary">Achievement Unlocked!</h3>
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            
            <p className="text-xl font-semibold">{achievement.name}</p>
            <p className="text-sm text-muted-foreground">{achievement.description}</p>
            
            <div className="flex items-center justify-center gap-2 pt-2">
              <Badge className="text-lg px-4 py-1">
                +{xpGained} XP
              </Badge>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Simple Badge component for XP display
function Badge({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary ${className}`}>
      {children}
    </span>
  );
}
